import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ButtonGroup, { type ButtonGroupProps } from '@mui/material/ButtonGroup';
import type React from 'react';
import { useRef, useState } from 'react';

interface Props<Options extends string[]> extends ButtonGroupProps {
  options: Options;
  mainButton: (option: string, index: number) => React.JSX.Element;
  disablePortal?: boolean;
  initOptionIndex?: Options[number];
  buttonClassName?: string;
  onMenuSelect?: (index: number) => void;
}

const SplitButton = <Options extends string[]>({
  options,
  mainButton,
  disablePortal,
  initOptionIndex,
  onMenuSelect,
  ...btnGroupProps
}: Props<Options>) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(
    initOptionIndex && options.indexOf(initOptionIndex) ? options.indexOf(initOptionIndex) : 0
  );

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) => {
    onMenuSelect?.(index);
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) return;
    setOpen(false);
  };

  return (
    <>
      <ButtonGroup ref={anchorRef} aria-label="split button" {...btnGroupProps}>
        {mainButton(options[selectedIndex], selectedIndex)}
        <Button
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="split button"
          aria-haspopup="menu"
          {...(!!btnGroupProps?.fullWidth ? { style: { width: 'unset' } } : {})}
          onClick={handleToggle}
          className={btnGroupProps?.buttonClassName}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal={disablePortal}
        style={{ zIndex: 100000 }}
        placement="bottom-end"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu">
                  {options.map((option, index) => (
                    <MenuItem
                      key={option}
                      disabled={index === selectedIndex}
                      selected={index === selectedIndex}
                      onClick={(event) => {
                        handleMenuItemClick(event, index);
                      }}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default SplitButton;
